import { useState } from 'react';
import { columns, realItems } from '@/mocks/gossip';

function SectionHead({
  kicker,
  title,
  sub,
  light,
}: {
  kicker: string;
  title: string;
  sub: string;
  light?: boolean;
}) {
  return (
    <div className="flex flex-col items-center gap-2 text-center">
      <span
        className={`text-sm font-semibold ${light ? 'text-accent-300' : 'text-primary-500'}`}
      >
        {kicker}
      </span>
      <h2
        className={`font-display text-3xl md:text-4xl ${
          light ? 'text-white' : 'text-foreground-950'
        }`}
      >
        {title}
      </h2>
      <p className={`max-w-xl text-sm md:text-base ${light ? 'text-white/80' : 'text-foreground-600'}`}>
        {sub}
      </p>
    </div>
  );
}

const cardClassMap: Record<string, string> = {
  primary: 'bg-primary-100 text-primary-800',
  accent: 'bg-accent-100 text-accent-800',
  secondary: 'bg-secondary-100 text-secondary-800',
};

export function ColumnsSection() {
  return (
    <section id="columns" className="bg-background-100/70 py-14 md:py-20">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <SectionHead
          kicker="COLUMNS · 栏目专区"
          title="对症下饭，总有一盘合你胃口"
          sub="从早八速食到深夜下饭，不同栏目满足不同时段的吃瓜胃口，配饭下饭更有章法。"
        />
        <div className="mt-9 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 md:gap-5">
          {columns.map((c) => (
            <a
              key={c.id}
              href="#feed"
              className="group overflow-hidden rounded-2xl border border-background-200 bg-background-50 transition-transform hover:-translate-y-1"
            >
              <div className="relative h-40 overflow-hidden">
                <img
                  src={c.image}
                  alt={`吃瓜cb栏目-${c.name}`}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <span
                  className={`absolute left-3 top-3 rounded-full px-3 py-1 text-xs font-bold ${cardClassMap[c.accent]}`}
                >
                  {c.count}
                </span>
              </div>
              <div className="p-4">
                <h3 className="font-display text-lg text-foreground-950">{c.name}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{c.desc}</p>
                <span className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-primary-600">
                  进入专栏
                  <i className="ri-arrow-right-line transition-transform group-hover:translate-x-1"></i>
                </span>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

export function RealSection() {
  return (
    <section id="real" className="relative overflow-hidden py-14 md:py-20">
      <div className="absolute inset-0 bg-primary-700"></div>
      <img
        src="https://readdy.ai/api/search-image?query=stylized%20abstract%20warm%20orange%20and%20gold%20flowing%20ribbons%20with%20soft%20light%20bokeh%20flat%20background%2C%20playful%20cheerful%20texture%2C%20clean%20minimal&width=1600&height=520&seq=real-band&orientation=landscape"
        alt="吃瓜cb真香现场暖色氛围背景"
        className="absolute inset-0 h-full w-full object-cover opacity-20"
      />
      <div className="relative mx-auto max-w-7xl px-4 py-4 md:px-6">
        <SectionHead
          light
          kicker="TRUE EXPERIENCE · 真香现场"
          title="他们说：这瓜是真的下饭"
          sub="来自瓜友的真实反馈，把「边吃边看真香现场」刻进 DNA，开动好奇心，越刷越上头。"
        />
        <div className="mt-9 grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-5">
          {realItems.map((r) => (
            <article
              key={r.id}
              className="group relative h-72 overflow-hidden rounded-2xl md:h-80"
            >
              <img
                src={r.image}
                alt={r.title}
                loading="lazy"
                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/85 via-foreground-950/25 to-transparent"></div>
              <div className="absolute inset-x-0 bottom-0 p-5">
                <span className="rounded-full bg-accent-400 px-3 py-1 text-xs font-bold text-foreground-950">
                  {r.tag}
                </span>
                <h3 className="mt-3 font-display text-xl leading-snug text-white">{r.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-white/85">
                  <i className="ri-double-quotes-l mr-1 text-accent-300"></i>
                  {r.quote}
                </p>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}